import type { Scene } from '@babylonjs/core/scene';
import { Constants } from '@babylonjs/core/Engines/constants';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import '@babylonjs/core/Particles/particleSystemComponent';
import type { GoldenShineConfig } from '../config/effects.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { TrackPresenter } from '../level/TrackPresenter';
import { clamp01, DEG_TO_RAD, lerp, TAU } from '../utils/math';
import type { CollectibleInstance } from './CollectibleInstance';
import { CollectibleType } from './CollectibleType';

/** Texture sizes (px) of the drawn sprites – no texture files needed. */
const HALO_TEXTURE = 128;
const GLINT_TEXTURE = 256;
const SPARKLE_TEXTURE = 64;
/** ParticleSystem.manualEmitCount value that means "emit by emitRate" (see DustEffect). */
const RATE_EMISSION = -1;
/** Short diagonal rays of the glint, relative to the long ones. */
const SHORT_RAY = 0.5;
/** Ray width at the centre, share of the texture size (long / short rays). */
const RAY_WIDTH = { long: 0.02, short: 0.012 } as const;

/** The golden bottle shines; every other collectible is left alone. */
export const shines = (item: CollectibleInstance): boolean => item.definition.type === CollectibleType.GoldenBottle;

/**
 * Shine of the golden bottle (SEG_GOLDEN_RAMP_60M): a breathing golden halo and a slowly turning star glint
 * that twinkles now and then – unlit billboards outside the fog that grow with the camera distance, so the
 * bottle is seen from far away (also at night) – plus sparkles rising around it. On pickup the halo flares out and the
 * sparkles burst. Presentation only: it follows the collectible events, gameplay never calls it.
 * One set of nodes, created once and moved to the bottle that spawns (one golden bottle at a time).
 */
export class GoldenShinePresenter {
  private readonly root: TransformNode;
  private readonly halo: Mesh;
  private readonly glint: Mesh;
  private readonly haloMaterial: StandardMaterial;
  private readonly glintMaterial: StandardMaterial;
  private readonly glintTexture: DynamicTexture;
  private readonly sparkles: ParticleSystem;
  private readonly emitter = new Vector3();
  /** The bottle the shine sits on (until its segment goes or the pickup flare is over). */
  private item: CollectibleInstance | null = null;
  /** Pickup flare: render time it started (NaN = pending, starts with the next frame), null = still shining. */
  private flareStart: number | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly track: TrackPresenter,
    private readonly config: GoldenShineConfig,
    events: EventBus<GameEvents>,
  ) {
    const color = Color3.FromHexString(config.color);
    this.root = new TransformNode('FX_GOLDEN_SHINE', scene);
    // The halo tints what lies behind it (a golden aura shows in front of the bright evening sky, where
    // added light would vanish); the glint adds light – it sparkles on any background.
    this.haloMaterial = glowMaterial('MAT_FX_GOLDEN_HALO', haloTexture(scene), color, Constants.ALPHA_COMBINE, scene);
    this.glintTexture = glintTexture(scene, config.glint.rays);
    this.glintMaterial = glowMaterial('MAT_FX_GOLDEN_GLINT', this.glintTexture, color, Constants.ALPHA_ADD, scene);
    this.halo = billboard('FX_GOLDEN_HALO', config.halo.size, this.haloMaterial, this.root, scene);
    this.glint = billboard('FX_GOLDEN_GLINT', config.glint.size, this.glintMaterial, this.root, scene);
    this.sparkles = this.createSparkles(scene, color);
    this.root.setEnabled(false);

    events.on('collectible:spawned', ({ collectible }) => {
      if (shines(collectible)) this.show(collectible);
    });
    events.on('collectible:despawned', ({ collectible }) => {
      if (collectible === this.item) this.hide();
    });
    events.on('collectible:collected', ({ collectible }) => {
      if (collectible === this.item && this.flareStart === null) this.pickUp(collectible.scale);
    });
  }

  render(time: number): void {
    if (!this.item) return;
    this.root.computeWorldMatrix(true);
    this.emitter.copyFrom(this.root.getAbsolutePosition());
    if (this.flareStart !== null) {
      this.renderFlare(time);
      return;
    }
    const { halo, glint } = this.config;
    const far = this.farBoost();
    const breath = 0.5 + 0.5 * Math.sin(time * halo.pulseHz * TAU);
    this.halo.scaling.setAll(far * (1 + halo.pulse * (2 * breath - 1)));
    this.haloMaterial.alpha = lerp(halo.opacity.min, halo.opacity.max, breath);
    this.glintTexture.wAng = time * glint.degreesPerSecond * DEG_TO_RAD;
    const sinceFlash = time % glint.flashPeriod;
    const twinkle = sinceFlash < glint.flashSeconds ? 1 - sinceFlash / glint.flashSeconds : 0;
    this.glintMaterial.alpha = glint.opacity * (1 + (glint.flashBoost - 1) * twinkle);
    this.glint.scaling.setAll(far * (1 + 0.5 * (glint.flashBoost - 1) * twinkle));
  }

  /** Size factor by camera distance (GoldenShineConfig.farBoost). */
  private farBoost(): number {
    const camera = this.scene.activeCamera;
    if (!camera) return 1;
    const { from, to, scale } = this.config.farBoost;
    const distance = Vector3.Distance(camera.globalPosition, this.emitter);
    return lerp(1, scale, clamp01((distance - from) / Math.max(to - from, Number.EPSILON)));
  }

  private show(item: CollectibleInstance): void {
    this.item = item;
    this.flareStart = null;
    const p = item.segment.placement(item.localPosition);
    this.root.parent = this.track.rootOf(item.segment);
    this.root.position.set(p.x, p.y + this.config.centerHeight * item.scale, p.z);
    this.root.scaling.setAll(item.scale);
    this.halo.setEnabled(true);
    this.glint.setEnabled(true);
    this.root.setEnabled(true);
    this.setSparkleScale(item.scale);
    this.setSparkleSpeed(this.config.sparkles.speed, item.scale);
    this.sparkles.manualEmitCount = RATE_EMISSION;
    this.sparkles.emitRate = this.config.sparkles.rate;
  }

  /** Bottle gone with its segment: everything off, detached before the segment root is disposed. */
  private hide(): void {
    this.item = null;
    this.flareStart = null;
    this.sparkles.emitRate = 0;
    this.root.setEnabled(false);
    this.root.parent = null;
  }

  /** Collected: the glint goes out, the halo flares, the sparkles burst outwards. */
  private pickUp(scale: number): void {
    this.flareStart = Number.NaN;
    this.glint.setEnabled(false);
    this.sparkles.emitRate = 0;
    this.setSparkleSpeed(this.config.sparkles.burstSpeed, scale);
    this.sparkles.manualEmitCount = this.config.sparkles.burst;
  }

  private renderFlare(time: number): void {
    if (Number.isNaN(this.flareStart)) this.flareStart = time;
    const { seconds, scale } = this.config.pickupFlare;
    const t = seconds > 0 ? clamp01((time - (this.flareStart ?? time)) / seconds) : 1;
    this.halo.scaling.setAll(lerp(1, scale, t));
    this.haloMaterial.alpha = this.config.halo.opacity.max * (1 - t);
    if (t >= 1) this.hide();
  }

  private createSparkles(scene: Scene, color: Color3): ParticleSystem {
    const c = this.config.sparkles;
    const system = new ParticleSystem('FX_GOLDEN_SPARKLES', c.capacity, scene);
    system.particleTexture = sparkleTexture(scene);
    system.emitter = this.emitter;
    system.direction1 = new Vector3(-0.5, 0.2, -0.5);
    system.direction2 = new Vector3(0.5, 1, 0.5);
    system.minLifeTime = c.lifetime.min;
    system.maxLifeTime = c.lifetime.max;
    system.minAngularSpeed = -2;
    system.maxAngularSpeed = 2;
    system.color1 = new Color4(color.r, color.g, color.b, 1);
    system.color2 = new Color4(1, 1, 1, 1);
    system.colorDead = new Color4(color.r, color.g, color.b, 0);
    system.blendMode = ParticleSystem.BLENDMODE_ADD;
    system.emitRate = 0;
    system.start();
    return system;
  }

  /** Emitter box, particle size and drift grow with the bottle. */
  private setSparkleScale(scale: number): void {
    const c = this.config.sparkles;
    this.sparkles.minEmitBox = new Vector3(-c.area.halfWidth * scale, -c.area.halfHeight * scale, -c.area.halfWidth * scale);
    this.sparkles.maxEmitBox = new Vector3(c.area.halfWidth * scale, c.area.halfHeight * scale, c.area.halfWidth * scale);
    this.sparkles.minSize = c.size.min * scale;
    this.sparkles.maxSize = c.size.max * scale;
    this.sparkles.gravity = new Vector3(0, c.rise * scale, 0);
  }

  private setSparkleSpeed(speed: { readonly min: number; readonly max: number }, scale: number): void {
    this.sparkles.minEmitPower = speed.min * scale;
    this.sparkles.maxEmitPower = speed.max * scale;
  }
}

/** Unlit glow sprite: colour × sprite (unlit = emissive only), untouched by fog and shadows (FX_ meshes cast none). */
function glowMaterial(name: string, texture: DynamicTexture, color: Color3, alphaMode: number, scene: Scene): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.diffuseTexture = texture;
  material.useAlphaFromDiffuseTexture = true;
  material.diffuseColor = Color3.Black();
  material.emissiveColor = color;
  material.specularColor = Color3.Black();
  material.disableLighting = true;
  material.backFaceCulling = false;
  material.fogEnabled = false;
  material.disableDepthWrite = true;
  material.alphaMode = alphaMode;
  return material;
}

function billboard(name: string, size: number, material: StandardMaterial, parent: TransformNode, scene: Scene): Mesh {
  const mesh = CreatePlane(name, { size }, scene);
  mesh.material = material;
  mesh.parent = parent;
  mesh.billboardMode = TransformNode.BILLBOARDMODE_ALL;
  mesh.isPickable = false;
  mesh.applyFog = false;
  return mesh;
}

/** Soft round glow: bright core, long falloff. */
function haloTexture(scene: Scene): DynamicTexture {
  const texture = new DynamicTexture('TEX_FX_GOLDEN_HALO', HALO_TEXTURE, scene, true);
  const ctx = texture.getContext();
  const half = HALO_TEXTURE / 2;
  const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.3, 'rgba(255,255,255,0.62)');
  gradient.addColorStop(0.65, 'rgba(255,255,255,0.2)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, HALO_TEXTURE, HALO_TEXTURE);
  texture.hasAlpha = true;
  texture.update();
  return texture;
}

/** Star glint: `rays` long rays with short ones in between, fading towards their tips. */
function glintTexture(scene: Scene, rays: number): DynamicTexture {
  const texture = new DynamicTexture('TEX_FX_GOLDEN_GLINT', GLINT_TEXTURE, scene, true);
  const ctx = texture.getContext();
  const c = GLINT_TEXTURE / 2;
  ctx.clearRect(0, 0, GLINT_TEXTURE, GLINT_TEXTURE);
  for (let i = 0; i < 2 * rays; i++) {
    const long = i % 2 === 0;
    const angle = (i * Math.PI) / rays;
    const length = c * (long ? 1 : SHORT_RAY);
    const half = GLINT_TEXTURE * (long ? RAY_WIDTH.long : RAY_WIDTH.short);
    const tipX = c + Math.cos(angle) * length;
    const tipY = c + Math.sin(angle) * length;
    const gradient = ctx.createLinearGradient(c, c, tipX, tipY);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(c - Math.sin(angle) * half, c + Math.cos(angle) * half);
    ctx.lineTo(tipX, tipY);
    ctx.lineTo(c + Math.sin(angle) * half, c - Math.cos(angle) * half);
    ctx.closePath();
    ctx.fill();
  }
  texture.hasAlpha = true;
  texture.wrapU = Texture.CLAMP_ADDRESSMODE;
  texture.wrapV = Texture.CLAMP_ADDRESSMODE;
  texture.update();
  return texture;
}

/** Small four-pointed sparkle with a round core. */
function sparkleTexture(scene: Scene): DynamicTexture {
  const texture = new DynamicTexture('TEX_FX_GOLDEN_SPARKLE', SPARKLE_TEXTURE, scene, false);
  const ctx = texture.getContext();
  const c = SPARKLE_TEXTURE / 2;
  ctx.clearRect(0, 0, SPARKLE_TEXTURE, SPARKLE_TEXTURE);
  const core = ctx.createRadialGradient(c, c, 0, c, c, c * 0.45);
  core.addColorStop(0, 'rgba(255,255,255,1)');
  core.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = core;
  ctx.fillRect(0, 0, SPARKLE_TEXTURE, SPARKLE_TEXTURE);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.beginPath();
  ctx.moveTo(c, 0);
  ctx.lineTo(c + c * 0.12, c);
  ctx.lineTo(c, 2 * c);
  ctx.lineTo(c - c * 0.12, c);
  ctx.closePath();
  ctx.moveTo(0, c);
  ctx.lineTo(c, c - c * 0.12);
  ctx.lineTo(2 * c, c);
  ctx.lineTo(c, c + c * 0.12);
  ctx.closePath();
  ctx.fill();
  texture.hasAlpha = true;
  texture.update();
  return texture;
}
